/**
 * Blackbox — a hidden rule, some worked evidence, and ten test words to
 * classify. The player probes words to learn the rule before committing.
 *
 * Ported from iOS `BlackboxPuzzle.swift` / `BlackboxGameSession.swift`.
 */

export type Classification = 'follows' | 'breaks' | 'skipped';
export type ProbeResult = 'follows' | 'breaks' | 'notRecognized';

export interface Probe {
  word: string;
  result: ProbeResult;
}

export interface BlackboxPuzzle {
  ruleDisplayText: string;
  ruleFamily: string;
  ruleDifficulty: string;
  /** Six worked examples that satisfy the rule. */
  positives: string[];
  /** Four that don't. */
  negatives: string[];
  testWords: string[];
  testWordAnswers: boolean[];
  /** Every dictionary word satisfying the rule — lets probes resolve offline. */
  positiveSet: Set<string>;
}

/** Raw Firestore shape. */
export interface BlackboxLevel {
  ruleDisplayText?: string;
  ruleFamily?: string;
  ruleDifficulty?: string;
  ruleId?: string;
  positives?: string[];
  negatives?: string[];
  testWords?: string[];
  testWordAnswers?: boolean[];
  positiveWords?: string[];
}

/** Probe budget before any hint-granted extras. */
export const PROBE_BUDGET = 6;

/** Quest mode demands 80% correct to advance. */
export const QUEST_PASS_RATIO = 0.8;

export function requiredCorrect(total: number): number {
  return Math.ceil(total * QUEST_PASS_RATIO);
}

export function normalizePuzzle(raw: BlackboxLevel | null): BlackboxPuzzle | null {
  if (!raw) return null;

  const testWords = (raw.testWords ?? []).map((w) => String(w).toUpperCase());
  const testWordAnswers = (raw.testWordAnswers ?? []).map(Boolean);
  if (!testWords.length || testWordAnswers.length !== testWords.length) return null;

  const ruleDisplayText = (raw.ruleDisplayText ?? '').trim();
  if (!ruleDisplayText) return null;

  return {
    ruleDisplayText,
    ruleFamily: raw.ruleFamily ?? 'structural',
    ruleDifficulty: raw.ruleDifficulty ?? 'medium',
    positives: (raw.positives ?? []).map((w) => String(w).toUpperCase()),
    negatives: (raw.negatives ?? []).map((w) => String(w).toUpperCase()),
    testWords,
    testWordAnswers,
    positiveSet: new Set((raw.positiveWords ?? []).map((w) => String(w).toUpperCase())),
  };
}

/**
 * Resolve a probe. Words in the rule's positive set follow it; other real words
 * break it; anything the dictionary doesn't know is "not recognized" and costs
 * the player nothing.
 */
export function evaluateProbe(
  puzzle: BlackboxPuzzle,
  word: string,
  isRecognizedWord: (w: string) => boolean,
): ProbeResult {
  const upper = word.toUpperCase();
  if (puzzle.positiveSet.has(upper)) return 'follows';
  if (isRecognizedWord(upper)) return 'breaks';
  return 'notRecognized';
}

/** Only recognized probes draw down the budget. */
export function validProbeCount(probes: Probe[]): number {
  return probes.filter((p) => p.result !== 'notRecognized').length;
}

export function probesRemaining(probes: Probe[], extraProbes: number): number {
  return Math.max(0, PROBE_BUDGET + extraProbes - validProbeCount(probes));
}

/** How many test words the player classified correctly. */
export function tallyCorrect(
  puzzle: BlackboxPuzzle,
  classifications: Array<Classification | null>,
): number {
  let correct = 0;
  for (let i = 0; i < puzzle.testWords.length; i++) {
    const truth = puzzle.testWordAnswers[i] ?? false;
    const c = classifications[i];
    if ((c === 'follows' && truth) || (c === 'breaks' && !truth)) correct++;
  }
  return correct;
}
